// The comparator is SHARED (FNP-912) — nulls-last in both directions, numeric
// vs string, and empty-string-counts-as-missing live in one place. This module
// had its own copy until FNP-923; do not write another.
import {useTableSort} from '@flexinet/ui-components';

import {useDefectSignaturesInfiniteQuery} from './useDefectSignaturesQuery';
import {useDefectSignaturesSearch} from './useDefectSignaturesSearch';
import {useDefectSignaturesFilter} from './useDefectSignaturesFilter';
import {DefectSignature} from '../types';
// Only the DEFAULT stays local: WHICH key this table sorts by is a per-entity
// judgement, unlike HOW two values compare.
import {DEFAULT_SORT} from '../utils/sortDefects';
import {
    DefectSummary,
    summarizeDefects,
} from '../utils/summarizeDefects';

// Client-side keyword match. The server-side filter covers the structured fields
// (status, actionCode, verificationResult); the keyword is the free-text sweep
// across loaded rows — "show me everything mentioning sign-in".
//
// Searches the NORMALIZED error as well as the sample: an operator who saw
// "<epoch>" in the grouped view should be able to find it by typing it.
const matchesKeyword = (item: DefectSignature, needle: string): boolean => {
    if (!needle) {
        return true;
    }
    const haystack = [
        item.actionCode,
        item.status,
        item.outcome,
        item.verificationResult,
        item.sampleError,
        item.normalizedError,
        item.jiraKey,
    ];
    return haystack.some(value => typeof value === 'string' && value.toLowerCase()
                                                                    .includes(needle));
};

export const useDefectSignatures = () => {
    const {filters} = useDefectSignaturesFilter();
    const query = useDefectSignaturesInfiniteQuery(filters);

    // The keyword comes from the module-scoped store, so the value typed into the
    // toolbar in a SIBLING component actually reaches this filter (FNP-815).
    const {keyword} = useDefectSignaturesSearch();

    const items: DefectSignature[] = (query.data?.pages?.flatMap(page => page?.items ?? []) ?? [])
        .filter(Boolean);

    const needle = keyword.trim()
                          .toLowerCase();

    const visibleItems = items.filter(item => matchesKeyword(item, needle));

    // Sorted AFTER filtering so the ordering describes what is on screen.
    // Sort state is component-level by design (FNP-912): it is how ONE
    // operator is viewing this table, unlike the keyword and filters, which
    // are module-scoped because a sibling toolbar sets them.
    const {
        sortStatus,
        setSortStatus,
        sorted: records,
    } = useTableSort(visibleItems, DEFAULT_SORT);

    // Summarized from the VISIBLE rows, not every loaded one: a summary that
    // ignored the active filter would contradict the table beneath it.
    const summary: DefectSummary = summarizeDefects(visibleItems);

    return {
        items,
        visibleItems,
        records,
        summary,
        sortStatus,
        setSortStatus,
        loading: query.isLoading,
        isError: query.isError,
        nextToken: query.hasNextPage ? 'has-more' : null,
        isFetchingNextPage: query.isFetchingNextPage,
        fetchRecords: () => {
            if (query.hasNextPage && !query.isFetchingNextPage) {
                query.fetchNextPage();
            }
        },
        reload: () => query.refetch(),
    };
};
